import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Trash2, RotateCcw, Lock, Save, X, AlertTriangle, Flame } from 'lucide-react';
import { invalidateCasDoubleUsageCache } from '../components/DoubleUsageBlock';

const emptyForm = { code: '', libelle: '', ordre: 99 };

const AdminCasDoubleUsageTICGN = () => {
  const { hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});  // {id: {libelle, ordre}}
  const [savingId, setSavingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCas, setNewCas] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [resetting, setResetting] = useState(false);

  const canEdit = hasPermission && hasPermission('admin_tarifs', 'edit');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/cas-double-usage-ticgn');
      setItems(Array.isArray(data) ? data : []);
      const dr = {};
      (Array.isArray(data) ? data : []).forEach((c) => {
        dr[c.id] = { libelle: c.libelle ?? '', ordre: c.ordre ?? 99 };
      });
      setDrafts(dr);
    } catch (e) {
      toast.error('Erreur chargement des cas de double usage');
    } finally { setLoading(false); }
  };

  const isDirty = (cas) => {
    const d = drafts[cas.id];
    if (!d) return false;
    return String(d.libelle ?? '') !== String(cas.libelle ?? '') ||
      Number(d.ordre) !== Number(cas.ordre);
  };

  const handleSave = async (cas) => {
    const d = drafts[cas.id];
    if (!d) return;
    if (!d.libelle?.trim()) { toast.error('Libellé obligatoire'); return; }
    setSavingId(cas.id);
    try {
      await api.put(`/admin/cas-double-usage-ticgn/${cas.id}`, {
        libelle: d.libelle.trim(),
        ordre: Number(d.ordre) || 99,
      });
      toast.success(`Cas ${cas.code} sauvegardé`);
      invalidateCasDoubleUsageCache();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setSavingId(null); }
  };

  const handleDelete = async (cas) => {
    if (!window.confirm(`Supprimer le cas "${cas.libelle}" (${cas.code}) ?`)) return;
    setDeletingId(cas.id);
    try {
      await api.delete(`/admin/cas-double-usage-ticgn/${cas.id}`);
      toast.success('Cas supprimé');
      invalidateCasDoubleUsageCache();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    } finally { setDeletingId(null); }
  };

  const handleCreate = async () => {
    if (!newCas.code.trim() || !newCas.libelle.trim()) {
      toast.error('Code et libellé obligatoires'); return;
    }
    setCreating(true);
    try {
      await api.post('/admin/cas-double-usage-ticgn', {
        code: newCas.code.trim().toUpperCase(),
        libelle: newCas.libelle.trim(),
        ordre: Number(newCas.ordre) || 99,
      });
      toast.success('Cas créé');
      invalidateCasDoubleUsageCache();
      setNewCas(emptyForm);
      setShowAddModal(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally { setCreating(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser aux 4 cas réglementaires par défaut ? Vos cas personnalisés seront supprimés.')) return;
    setResetting(true);
    try {
      await api.post('/admin/cas-double-usage-ticgn/reset');
      toast.success('Référentiel réinitialisé');
      invalidateCasDoubleUsageCache();
      await load();
    } catch (e) {
      toast.error('Erreur réinitialisation');
    } finally { setResetting(false); }
  };

  if (loading) {
    return <div className="p-8 text-center text-[#687175]">Chargement…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="admin-cas-du-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-[#191D1E] flex items-center gap-2">
            <Flame className="w-6 h-6 text-[#F97316]" /> Cas de Double Usage TICGN
          </h1>
          <p className="text-sm text-[#687175] mt-1">
            Référentiel des cas de double usage applicables (art. 265 nonies du Code des douanes).
            Affiché dans la liste déroulante des PCE TICGN.
          </p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <>
              <button
                onClick={handleReset}
                disabled={resetting}
                className="flex items-center gap-1 px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm text-[#687175] hover:bg-[#F5F6F9] disabled:opacity-50"
                data-testid="admin-cas-du-reset-btn"
              >
                <RotateCcw className="w-4 h-4" /> Réinitialiser
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-1 px-3 py-2 bg-[#F97316] text-white rounded-lg text-sm hover:bg-[#FF6B35]"
                data-testid="admin-cas-du-add-btn"
              >
                <Plus className="w-4 h-4" /> Ajouter un cas
              </button>
            </>
          )}
        </div>
      </div>

      {!canEdit && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-sm text-amber-900">
          <Lock className="w-4 h-4" /> Lecture seule — vous n'avez pas la permission "admin_tarifs:edit".
        </div>
      )}

      <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F6F9] text-[#687175]">
            <tr>
              <th className="px-3 py-2 text-left">Ordre</th>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Libellé</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-[#687175]">Aucun cas défini.</td></tr>
            )}
            {items.map((cas) => {
              const d = drafts[cas.id] || { libelle: cas.libelle, ordre: cas.ordre };
              const dirty = canEdit && isDirty(cas);
              return (
                <tr key={cas.id} className="border-t border-[#F5F6F9]" data-testid={`admin-cas-du-row-${cas.code}`}>
                  <td className="px-3 py-2 w-20">
                    <input
                      type="number"
                      disabled={!canEdit}
                      value={d.ordre}
                      onChange={(e) => setDrafts({ ...drafts, [cas.id]: { ...d, ordre: e.target.value } })}
                      className="w-16 px-2 py-1 border border-[#BFC6CC] rounded text-sm disabled:bg-[#F5F6F9]"
                    />
                  </td>
                  <td className="px-3 py-2 font-mono text-[#F97316] w-56">{cas.code}</td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      disabled={!canEdit}
                      value={d.libelle}
                      onChange={(e) => setDrafts({ ...drafts, [cas.id]: { ...d, libelle: e.target.value } })}
                      className="w-full px-2 py-1 border border-[#BFC6CC] rounded text-sm disabled:bg-[#F5F6F9]"
                      data-testid={`admin-cas-du-libelle-${cas.code}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canEdit && (
                      <>
                        <button
                          onClick={() => handleSave(cas)}
                          disabled={!dirty || savingId === cas.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#10B981] text-white rounded disabled:opacity-50 mr-1"
                          data-testid={`admin-cas-du-save-${cas.code}`}
                        >
                          <Save className="w-3 h-3" /> {savingId === cas.id ? '…' : 'Enregistrer'}
                        </button>
                        <button
                          onClick={() => handleDelete(cas)}
                          disabled={deletingId === cas.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-500 text-white rounded disabled:opacity-50"
                          data-testid={`admin-cas-du-delete-${cas.code}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg" data-testid="admin-cas-du-add-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#191D1E]">Nouveau cas de double usage</h2>
              <button onClick={() => setShowAddModal(false)} className="text-[#687175]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-[#687175] mb-1">Code (UPPERCASE) *</label>
                <input
                  type="text"
                  value={newCas.code}
                  onChange={(e) => setNewCas({ ...newCas, code: e.target.value.toUpperCase() })}
                  placeholder="DU_CUSTOM_X"
                  className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm font-mono"
                  data-testid="admin-cas-du-new-code"
                />
              </div>
              <div>
                <label className="block text-xs text-[#687175] mb-1">Libellé *</label>
                <input
                  type="text"
                  value={newCas.libelle}
                  onChange={(e) => setNewCas({ ...newCas, libelle: e.target.value })}
                  className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm"
                  data-testid="admin-cas-du-new-libelle"
                />
              </div>
              <div>
                <label className="block text-xs text-[#687175] mb-1">Ordre d'affichage</label>
                <input
                  type="number"
                  value={newCas.ordre}
                  onChange={(e) => setNewCas({ ...newCas, ordre: e.target.value })}
                  className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm text-[#687175]"
              >Annuler</button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-3 py-2 bg-[#F97316] text-white rounded-lg text-sm disabled:opacity-50"
                data-testid="admin-cas-du-new-submit"
              >{creating ? 'Création…' : 'Créer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCasDoubleUsageTICGN;
